import React, { useEffect, useState } from "react";
import { X, Maximize2, Minimize2, Radio } from "lucide-react";
import { LiveKitRoom, RoomAudioRenderer, StartAudio, useTracks, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";

const API = process.env.REACT_APP_BACKEND_URL;

// Minimal video surface for the floating co-view window (screen share preferred, then camera)
const PiPVideo = () => {
  const tracks = useTracks([Track.Source.ScreenShare, Track.Source.Camera]);
  const remote = tracks.find((t) => !t.participant.isLocal) || tracks[0];
  if (!remote) {
    return <p className="text-[11px] font-bold text-[#9CA3AF] m-auto text-center px-3">Waiting for the host's video…</p>;
  }
  return <VideoTrack trackRef={remote} className="w-full h-full object-cover" />;
};

// Floating picture-in-picture live-event co-view: keeps streaming while the user navigates.
// Open from anywhere: window.dispatchEvent(new CustomEvent("nt-pip-live", { detail: { eventId, title } }))
export default function LivePiP({ eventId, title, onClose }) {
  const [join, setJoin] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/api/livekit/rooms/${eventId}/join`, { method: "POST", credentials: "include" })
      .then(async (r) => {
        const b = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(typeof b.detail === "string" ? b.detail : "Video unavailable");
        if (live) setJoin(b);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [eventId]);

  return (
    <div className={expanded ? "fixed inset-0 z-[75] bg-[#111827]/95 flex items-center justify-center p-3 sm:p-6" : "fixed bottom-4 right-4 z-[75] w-72 sm:w-80"} data-testid="live-pip">
      <div className={`relative bg-black rounded-2xl overflow-hidden border border-[#374151] shadow-2xl ${expanded ? "w-full max-w-4xl h-[80dvh]" : "aspect-video"}`}>
        <div className="absolute top-0 inset-x-0 z-10 flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-b from-black/80 to-transparent">
          <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[9px] font-extrabold uppercase rounded-full animate-pulse shrink-0">
            <Radio className="w-2.5 h-2.5" /> Live
          </span>
          <p className="flex-1 text-[10px] font-bold text-white truncate">{title || "Live event"}</p>
          <button onClick={() => setExpanded((e) => !e)} data-testid="live-pip-expand" title={expanded ? "Minimize to floating player" : "Expand"} className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors">
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onClose} data-testid="live-pip-close" title="Stop watching" className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {error ? (
          <div className="w-full h-full flex items-center justify-center p-4" data-testid="live-pip-error">
            <p className="text-xs font-bold text-rose-300 text-center">{error}</p>
          </div>
        ) : !join ? (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-[11px] font-bold text-[#9CA3AF]">Connecting…</p>
          </div>
        ) : (
          <LiveKitRoom token={join.token} serverUrl={join.serverUrl} connect video={false} audio={false} onDisconnected={onClose} style={{ height: "100%" }}>
            <div className="w-full h-full flex">
              <PiPVideo />
            </div>
            <RoomAudioRenderer />
            <StartAudio label="Enable audio" />
          </LiveKitRoom>
        )}
      </div>
    </div>
  );
}
